import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Modal from '@mui/material/Modal';
import Icon from '@mui/material/Icon';
import moment from 'moment';
import 'moment/locale/es';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import { getSuscripcion } from 'actions/Suscripcion/suscripcion';
import { SubscriptionAlerts } from 'actions/Suscripcion/suscripcionTypes';

export type SelectedSubscriptionAlert = 'expiration7' | 'lowStamps' | 'expiration14' | null;

export function hasTerminalSubscriptionState(alerts: SubscriptionAlerts): boolean {
  return (
    alerts.lowStamps.availableStamps <= 0 ||
    (alerts.expiration.daysRemaining !== null && alerts.expiration.daysRemaining <= 0)
  );
}

export function selectSubscriptionAlert(alerts?: SubscriptionAlerts): SelectedSubscriptionAlert {
  if (!alerts) return null;
  // Los estados terminales ya son atendidos por RequireSuscriptionActive y
  // UpdateSubscription; no se deben duplicar en este modal preventivo.
  if (hasTerminalSubscriptionState(alerts)) return null;
  if (
    alerts.expiration.show &&
    alerts.expiration.daysRemaining !== null &&
    alerts.expiration.daysRemaining <= 7
  ) return 'expiration7';
  if (alerts.lowStamps.show) return 'lowStamps';
  if (alerts.expiration.show) return 'expiration14';
  return null;
}

const sessionKey = 'pxc-subscription-alert-checked';

export default function SubscriptionAlertModal(): JSX.Element | null {
  const [alerts, setAlerts] = useState<SubscriptionAlerts>();
  const [selected, setSelected] = useState<SelectedSubscriptionAlert>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(sessionKey) === '1') return;
    sessionStorage.setItem(sessionKey, '1');
    getSuscripcion()
      .then((response) => {
        const nextAlerts = response.data?.data?.alerts as SubscriptionAlerts | undefined;
        const nextSelected = selectSubscriptionAlert(nextAlerts);
        setAlerts(nextAlerts);
        setSelected(nextSelected);
        setOpen(nextSelected !== null);
      })
      .catch((error) => console.error('No se pudieron consultar los avisos de suscripción', error));
  }, []);

  if (!alerts || !selected) return null;

  const isLowStamps = selected === 'lowStamps';
  const daysRemaining = alerts.expiration.daysRemaining;
  const expirationDate = alerts.expiration.expirationDate
    ? moment(alerts.expiration.expirationDate).locale('es').format('D [de] MMMM [de] YYYY')
    : '';
  const title = isLowStamps
    ? 'Tus timbres están por agotarse'
    : selected === 'expiration7'
      ? `Tu suscripción vence en ${daysRemaining} días`
      : 'Tu suscripción está por vencer';
  const lowStampsMessage = alerts.lowStamps.usesMinimumThreshold
    ? `Actualmente tienes ${alerts.lowStamps.availableStamps} timbres disponibles. Para evitar que tu saldo llegue a cero, te avisamos al alcanzar el umbral preventivo de ${alerts.lowStamps.alertThreshold} timbres. Te recomendamos renovar o ampliar tu plan para evitar interrupciones al emitir comprobantes.`
    : `Actualmente tienes ${alerts.lowStamps.availableStamps} timbres disponibles y tu consumo promedio es de aproximadamente ${alerts.lowStamps.averageMonthlyUsage} timbres al mes. Te recomendamos renovar o ampliar tu plan para evitar interrupciones al emitir comprobantes.`;
  const message = isLowStamps
    ? lowStampsMessage
    : `Tu plan${alerts.planName ? ` ${alerts.planName}` : ' de Pixcua'} vence el ${expirationDate}. Te recomendamos renovarlo antes de esa fecha para evitar interrupciones en el servicio.`;

  return (
    <Modal open={open} onClose={() => setOpen(false)} aria-labelledby='subscription-alert-title'>
      <MDBox
        position='absolute'
        top='50%'
        left='50%'
        sx={{ transform: 'translate(-50%, -50%)' }}
        width={{ xs: '90%', sm: 520 }}
        bgcolor='background.paper'
        borderRadius='lg'
        shadow='xl'
        p={3}
      >
        <Alert severity='warning' icon={<Icon>{isLowStamps ? 'qr_code_2' : 'event'}</Icon>}>
          <MDTypography id='subscription-alert-title' variant='h6' mb={1}>
            {title}
          </MDTypography>
          <MDTypography variant='body2'>{message}</MDTypography>
        </Alert>
        <MDBox display='flex' justifyContent='flex-end' gap={1} mt={3}>
          <MDButton variant='text' color='secondary' onClick={() => setOpen(false)}>
            Cerrar
          </MDButton>
          <MDButton
            color='warning'
            onClick={() => {
              window.location.href = alerts.subscriptionUrl || '/configuracion/suscripciones';
            }}
          >
            Renovar o ampliar plan
          </MDButton>
        </MDBox>
      </MDBox>
    </Modal>
  );
}
